// NEW_FILE_JUSTIFIED: Shared step progress indicator for all insurance wizards (policies, claims, pre-auth, future forms) — replaces WizardStepProgress and ClaimStepProgress

import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import type { IStepProgressProps } from '@/@types/policyFormTemplates';

export function StepProgress({
  steps,
  currentStep,
  onStepClick,
  errorSteps,
  showAllLabels = true,
  fullWidth = true,
}: IStepProgressProps) {
  const errorSet = new Set(errorSteps ?? []);

  return (
    <nav
      aria-label="Form progress"
      className="w-full bg-background border-b border-border px-10 py-4"
    >
      <ol
        className={cn(
          'flex items-center w-full',
          fullWidth ? 'justify-between' : 'justify-center gap-0'
        )}
      >
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isError = errorSet.has(index);

          return (
            <li
              key={index}
              className={cn('flex items-center', fullWidth && 'flex-1 last:flex-none')}
            >
              <button
                type="button"
                onClick={() => onStepClick?.(index)}
                disabled={!onStepClick}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`Step ${index + 1}: ${step.title}${isCompleted ? ' (completed)' : ''}${isError ? ' (has errors)' : ''}`}
                className={cn(
                  'flex items-center gap-2',
                  onStepClick ? 'cursor-pointer' : 'cursor-default'
                )}
              >
                <span
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium transition-colors',
                    isError &&
                      !isCurrent &&
                      'bg-background border-2 border-destructive text-destructive',
                    isError &&
                      isCurrent &&
                      'bg-background border-2 border-destructive text-destructive ring-2 ring-destructive/30 ring-offset-2',
                    !isError && isCompleted && 'bg-lime-500 text-white',
                    !isError &&
                      isCurrent &&
                      'bg-lime-500 text-white ring-2 ring-lime-500 ring-offset-2',
                    !isError && !isCompleted && !isCurrent && 'bg-muted text-muted-foreground'
                  )}
                >
                  {isCompleted && !isError ? <Check className="h-3.5 w-3.5" /> : index + 1}
                </span>
                {showAllLabels ? (
                  <span
                    className={cn(
                      'text-xs whitespace-nowrap hidden sm:inline',
                      isError && 'text-destructive font-medium',
                      !isError && isCompleted && 'text-lime-700 font-medium',
                      !isError && isCurrent && 'text-foreground font-medium',
                      !isError && !isCompleted && !isCurrent && 'text-muted-foreground'
                    )}
                  >
                    {step.title}
                  </span>
                ) : (
                  isCurrent && (
                    <span className="hidden text-sm font-medium whitespace-nowrap text-foreground md:inline">
                      {step.title}
                    </span>
                  )
                )}
              </button>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'h-0.5 shrink-0',
                    fullWidth ? 'mx-3 flex-1' : 'mx-3 w-8',
                    isCompleted ? 'bg-lime-500' : 'bg-muted'
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
